import { T } from "@repo/transifex";
import { Badge } from "~/components/Badge";
import { type StockStatus } from "~/utils/stock/infer-stock-status";
import { getAvailabilityCount } from "~/utils/stock/get-availability-count";

type ProductAvailabilityBadgeProps = {
  stockStatus: StockStatus;
  isLimitOver: boolean;
  /** Remaining units the user can still add under their personal limit. */
  remaining: number | null;
  size?: "sm" | "md" | "lg";
};

/**
 * Picks the right availability badge for a kiosk product card.
 *
 * Priority order:
 *  1. Out of stock / all stock reserved in basket → "Sold out"
 *  2. User limit reached                          → "Limit reached"
 *  3. Low count (tighter of stock vs. user limit) → "X left"
 *  4. Otherwise                                   → null
 */
export const ProductAvailabilityBadge = ({
  stockStatus,
  isLimitOver,
  remaining,
  size = "sm"
}: ProductAvailabilityBadgeProps) => {
  if (stockStatus === "reserved" || stockStatus === "out-of-stock") {
    return (
      <Badge variant="danger" size={size}>
        <T _str="Sold out" />
      </Badge>
    );
  }

  if (isLimitOver) {
    return (
      <Badge variant="danger" size={size}>
        <T _str="Limit reached" />
      </Badge>
    );
  }

  const count = getAvailabilityCount(stockStatus, remaining);
  if (count !== null) {
    return (
      <Badge size={size}>
        <T _str="{amount} left" amount={count} />
      </Badge>
    );
  }

  return null;
};
